import { useCallback, useEffect, useRef, useState } from "react";
import LeadGroupsTab from "../components/campaigns/LeadGroupsTab";
import LeadManagementTab from "../components/leads/LeadManagementTab";
import useShellContext from "../layout/useShellContext";
import { getLeadGroups } from "../services/leadGroupsApi";
import { formatNow } from "../utils/formatters";

export default function LeadManagementPage() {
  const { registerPageControls, resetPageControls, showToast } = useShellContext();
  const [activeSection, setActiveSection] = useState("contacts");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshRef = useRef(null);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await getLeadGroups();
      setGroups(data.items || []);
    } catch (error) {
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (activeSection === "groups") {
        await loadGroups();
      } else {
        await refreshRef.current?.();
      }
    } finally {
      setIsRefreshing(false);
      setLastUpdated(formatNow());
    }
  }, [activeSection, loadGroups]);

  useEffect(() => {
    if (activeSection === "groups") {
      loadGroups()
        .then(() => setLastUpdated(formatNow()))
        .catch(() => undefined);
    }
  }, [activeSection, loadGroups]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing,
    });
  }, [handleRefresh, lastUpdated, isRefreshing, registerPageControls]);

  useEffect(() => {
    return () => resetPageControls();
  }, [resetPageControls]);

  return (
    <div className="tab-panel active" id="panel-lead-management" role="tabpanel">
      <div className="campaign-tabs">
        <button
          className={`campaign-tab-btn${activeSection === "contacts" ? " active" : ""}`}
          onClick={() => setActiveSection("contacts")}
        >
          Contacts / Leads
        </button>
        <button
          className={`campaign-tab-btn${activeSection === "groups" ? " active" : ""}`}
          onClick={() => setActiveSection("groups")}
        >
          Groups
        </button>
      </div>

      {activeSection === "contacts" ? (
        <LeadManagementTab
          showToast={showToast}
          onRefreshReady={(fn) => { refreshRef.current = fn; }}
          onDataLoaded={() => setLastUpdated(formatNow())}
        />
      ) : (
        <LeadGroupsTab
          groups={groups}
          loading={groupsLoading}
          showToast={showToast}
          onGroupsChange={loadGroups}
        />
      )}
    </div>
  );
}
